import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import html2canvas from 'html2canvas';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Bug, Lightbulb, Camera, Loader2, Send, Eraser, Pencil, X, Paperclip, Flag,
  ArrowUpRight, Circle as CircleIcon, Square as SquareIcon, Type as TypeIcon, Maximize2, Minimize2, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { subscribeOverlay } from '@/lib/overlayState';

const PEN_COLORS = ['#ef4444', '#3b82f6', '#22c55e', '#eab308', '#111827'];
// Priority as a low→high heat scale so severity reads at a glance.
const PRIORITIES = [
  { value: 'low', label: 'Low', color: '#22c55e' },
  { value: 'medium', label: 'Medium', color: '#eab308' },
  { value: 'high', label: 'High', color: '#f97316' },
  { value: 'urgent', label: 'Urgent', color: '#ef4444' },
];
const TOOLS = [
  { value: 'pen', label: 'Draw', Icon: Pencil },
  { value: 'arrow', label: 'Arrow', Icon: ArrowUpRight },
  { value: 'rect', label: 'Box', Icon: SquareIcon },
  { value: 'circle', label: 'Circle', Icon: CircleIcon },
  { value: 'text', label: 'Text', Icon: TypeIcon },
];
const MAX_ATTACH_BYTES = 10 * 1024 * 1024;
const ALLOWED_ATTACH = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif', 'application/pdf'];

export default function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [shot, setShot] = useState(null);        // annotated dataURL (final, submitted)
  const [baseShot, setBaseShot] = useState(null); // un-annotated dataURL
  const [capturing, setCapturing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [penColor, setPenColor] = useState(PEN_COLORS[0]);
  const [priority, setPriority] = useState('medium');
  const [attachment, setAttachment] = useState(null);
  const fileInputRef = useRef(null);

  // ── annotation model ──
  // Annotations are stored in NORMALISED (0..1) coords relative to the image so
  // toggling expand / resizing the canvas never distorts them.
  const [tool, setTool] = useState('pen');
  const [expanded, setExpanded] = useState(false);
  const [annCount, setAnnCount] = useState(0); // re-render trigger for undo/clear
  const annotationsRef = useRef([]);           // committed items
  const baseImgRef = useRef(null);             // cached HTMLImageElement of baseShot
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const startPt = useRef(null);                // normalised start for shapes
  const curPts = useRef(null);                 // normalised points for freehand
  const [textDraft, setTextDraft] = useState(null); // { nx, ny, px, py, value }

  const reset = () => {
    setType('bug'); setTitle(''); setDescription('');
    setShot(null); setBaseShot(null); setPenColor(PEN_COLORS[0]);
    setPriority('medium'); setAttachment(null);
    setTool('pen'); setExpanded(false); setTextDraft(null);
    annotationsRef.current = []; baseImgRef.current = null; setAnnCount(0);
  };

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file) return;
    if (!ALLOWED_ATTACH.includes(file.type)) { toast.error('Attach an image or PDF file'); return; }
    if (file.size > MAX_ATTACH_BYTES) { toast.error('File is too large — max 10 MB'); return; }
    const reader = new FileReader();
    reader.onload = () => setAttachment({ name: file.name, content_type: file.type, data: String(reader.result) });
    reader.onerror = () => toast.error('Could not read that file');
    reader.readAsDataURL(file);
  };

  // ── draggable launcher ──
  const btnRef = useRef(null);
  const dragRef = useRef(null);
  const [fabPos, setFabPos] = useState(null);
  const [overlayActive, setOverlayActive] = useState(false);
  useEffect(() => subscribeOverlay((n) => setOverlayActive(n > 0)), []);

  useEffect(() => {
    let local = null;
    try {
      const s = JSON.parse(localStorage.getItem('feedbackFabPosRB') || 'null');
      if (s && typeof s.right === 'number' && typeof s.bottom === 'number') { local = s; setFabPos(s); }
    } catch { /* ignore */ }
    // Reconcile with the account-level position (follows the user across
    // devices). Whichever was saved most recently wins — so a fresh drag on
    // THIS device is never clobbered by a stale server value whose sync didn't
    // land before the page closed — and we heal the loser so both ends agree.
    (async () => {
      try {
        const me = await api.getMe();
        const p = me?.ui_prefs?.feedback_fab_pos;
        const serverValid = p && typeof p.right === 'number' && typeof p.bottom === 'number';
        const localTs = local?.ts || 0;
        const serverTs = (serverValid && p.ts) || 0;
        if (serverValid && serverTs >= localTs) {
          const np = { right: p.right, bottom: p.bottom, ts: serverTs };
          setFabPos(np);
          try { localStorage.setItem('feedbackFabPosRB', JSON.stringify(np)); } catch { /* ignore */ }
        } else if (local && localTs > serverTs) {
          Promise.resolve(api.setUiPref('feedback_fab_pos', local)).catch(() => {});
        }
      } catch { /* ignore */ }
    })();
    // Anchor by distance from the RIGHT and BOTTOM edges so the launcher keeps
    // the same relative spot on any screen / window size.
    const clampRB = (r, b) => {
      const sz = 24, m = 4;
      return {
        right: Math.max(m, Math.min(r, window.innerWidth - sz - m)),
        bottom: Math.max(m, Math.min(b, window.innerHeight - sz - m)),
      };
    };
    const move = (e) => {
      const d = dragRef.current; if (!d) return;
      const dx = e.clientX - d.startX, dy = e.clientY - d.startY;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) d.moved = true;
      if (!d.moved) return;
      // moving right/down reduces the distance to the right/bottom edge
      const p = clampRB(d.originR - dx, d.originB - dy);
      d.cur = p; setFabPos(p);
    };
    const up = () => {
      const d = dragRef.current; if (!d) return;
      if (d.moved && d.cur) {
        const saved = { right: d.cur.right, bottom: d.cur.bottom, ts: Date.now() };
        setFabPos(saved);
        try { localStorage.setItem('feedbackFabPosRB', JSON.stringify(saved)); } catch { /* ignore */ }
        Promise.resolve(api.setUiPref('feedback_fab_pos', saved)).catch(() => {});
      }
      else { window.setTimeout(() => setOpen(true), 80); }
      dragRef.current = null;
    };
    const onResize = () => setFabPos(p => (p ? clampRB(p.right, p.bottom) : p));
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const startFabDrag = (e) => {
    // When another Radix modal is open it (a) sets pointer-events:none on the
    // body and (b) listens for outside pointerdowns to auto-dismiss. Stopping
    // propagation here keeps the underlying popup open and lets our own
    // pointerup open the feedback dialog on top of it.
    e.stopPropagation();
    const rect = btnRef.current.getBoundingClientRect();
    dragRef.current = {
      startX: e.clientX, startY: e.clientY,
      originR: window.innerWidth - rect.right, originB: window.innerHeight - rect.bottom,
      moved: false, cur: null,
    };
  };

  // ── canvas sizing + rendering ──
  const sizeCanvas = useCallback(() => {
    const canvas = canvasRef.current, img = baseImgRef.current;
    if (!canvas || !img) return;
    const maxW = expanded
      ? Math.min(img.width, Math.max(360, window.innerWidth - 96))
      : 760;
    const scale = Math.min(1, maxW / img.width);
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
  }, [expanded]);

  const drawItem = (ctx, item, w, h) => {
    ctx.strokeStyle = item.color; ctx.fillStyle = item.color;
    ctx.lineWidth = item.tool === 'pen' ? 3 : 3;
    ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    if (item.tool === 'pen') {
      const pts = item.points || [];
      if (pts.length < 2) return;
      ctx.beginPath();
      ctx.moveTo(pts[0].x * w, pts[0].y * h);
      for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x * w, pts[i].y * h);
      ctx.stroke();
    } else if (item.tool === 'rect') {
      const x = item.x0 * w, y = item.y0 * h;
      ctx.strokeRect(x, y, (item.x1 - item.x0) * w, (item.y1 - item.y0) * h);
    } else if (item.tool === 'circle') {
      const cx = ((item.x0 + item.x1) / 2) * w, cy = ((item.y0 + item.y1) / 2) * h;
      const rx = Math.abs(item.x1 - item.x0) / 2 * w, ry = Math.abs(item.y1 - item.y0) / 2 * h;
      ctx.beginPath(); ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2); ctx.stroke();
    } else if (item.tool === 'arrow') {
      const x0 = item.x0 * w, y0 = item.y0 * h, x1 = item.x1 * w, y1 = item.y1 * h;
      ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x1, y1); ctx.stroke();
      const ang = Math.atan2(y1 - y0, x1 - x0), head = 12;
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x1 - head * Math.cos(ang - Math.PI / 6), y1 - head * Math.sin(ang - Math.PI / 6));
      ctx.moveTo(x1, y1);
      ctx.lineTo(x1 - head * Math.cos(ang + Math.PI / 6), y1 - head * Math.sin(ang + Math.PI / 6));
      ctx.stroke();
    } else if (item.tool === 'text') {
      const fs = Math.max(14, Math.round(h * 0.03));
      ctx.font = `600 ${fs}px system-ui, sans-serif`;
      ctx.textBaseline = 'top';
      ctx.fillText(item.text || '', item.x * w, item.y * h);
    }
  };

  const redraw = useCallback((preview) => {
    const canvas = canvasRef.current, img = baseImgRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width, h = canvas.height;
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    for (const it of annotationsRef.current) drawItem(ctx, it, w, h);
    if (preview) drawItem(ctx, preview, w, h);
  }, []);

  // Load the base screenshot into an Image once, then render.
  useEffect(() => {
    if (!open || !baseShot) return;
    const img = new Image();
    img.onload = () => { baseImgRef.current = img; sizeCanvas(); redraw(); };
    img.src = baseShot;
  }, [open, baseShot, sizeCanvas, redraw]);

  // Re-size + repaint when toggling expand.
  useEffect(() => {
    if (!baseImgRef.current) return;
    const t = setTimeout(() => { sizeCanvas(); redraw(); }, 30);
    return () => clearTimeout(t);
  }, [expanded, sizeCanvas, redraw]);

  const commitShot = () => { if (canvasRef.current) setShot(canvasRef.current.toDataURL('image/jpeg', 0.85)); };

  const captureScreenshot = async () => {
    setCapturing(true);
    setOpen(false);
    await new Promise(r => setTimeout(r, 450));
    try {
      const bodyBg = getComputedStyle(document.body).backgroundColor;
      const canvas = await html2canvas(document.documentElement, {
        backgroundColor: (bodyBg && bodyBg !== 'rgba(0, 0, 0, 0)') ? bodyBg : '#0a0a0b',
        useCORS: true, allowTaint: true, logging: false, scale: 1,
        width: window.innerWidth, height: window.innerHeight,
        windowWidth: window.innerWidth, windowHeight: window.innerHeight,
        x: window.scrollX, y: window.scrollY, scrollX: 0, scrollY: 0,
        ignoreElements: (el) => !!(el.getAttribute && el.getAttribute('data-html2canvas-ignore') !== null),
      });
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
      annotationsRef.current = []; setAnnCount(0);
      setBaseShot(dataUrl);
      setShot(dataUrl);
    } catch (e) {
      toast.error('Could not capture the screen');
    } finally {
      setCapturing(false);
      setOpen(true);
    }
  };

  // ── pointer handling ──
  const pointerNorm = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const cx = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const cy = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    return { x: Math.min(1, Math.max(0, cx / rect.width)), y: Math.min(1, Math.max(0, cy / rect.height)),
             px: cx, py: cy };
  };

  const startDraw = (e) => {
    if (textDraft) return; // finish text entry first
    if (tool === 'text') return; // text is placed on click, not drag
    const p = pointerNorm(e);
    drawing.current = true;
    if (tool === 'pen') curPts.current = [{ x: p.x, y: p.y }];
    else startPt.current = { x: p.x, y: p.y };
  };
  const canvasClick = (e) => {
    if (tool !== 'text' || textDraft) return;
    const p = pointerNorm(e);
    setTextDraft({ nx: p.x, ny: p.y, px: p.px, py: p.py, value: '' });
  };
  const moveDraw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = pointerNorm(e);
    if (tool === 'pen') {
      curPts.current.push({ x: p.x, y: p.y });
      redraw({ tool: 'pen', color: penColor, points: curPts.current });
    } else {
      redraw({ tool, color: penColor, x0: startPt.current.x, y0: startPt.current.y, x1: p.x, y1: p.y });
    }
  };
  const endDraw = (e) => {
    if (!drawing.current) return;
    drawing.current = false;
    const p = pointerNorm(e);
    if (tool === 'pen') {
      if ((curPts.current || []).length >= 2) annotationsRef.current.push({ tool: 'pen', color: penColor, points: curPts.current });
      curPts.current = null;
    } else if (startPt.current) {
      const s = startPt.current;
      if (Math.abs(p.x - s.x) > 0.005 || Math.abs(p.y - s.y) > 0.005) {
        annotationsRef.current.push({ tool, color: penColor, x0: s.x, y0: s.y, x1: p.x, y1: p.y });
      }
      startPt.current = null;
    }
    setAnnCount(annotationsRef.current.length);
    redraw(); commitShot();
  };

  const commitText = () => {
    const t = textDraft;
    if (t && t.value.trim()) {
      annotationsRef.current.push({ tool: 'text', color: penColor, x: t.nx, y: t.ny, text: t.value.trim() });
      setAnnCount(annotationsRef.current.length);
    }
    setTextDraft(null);
    setTimeout(() => { redraw(); commitShot(); }, 0);
  };

  const undoAnnotation = () => {
    annotationsRef.current = annotationsRef.current.slice(0, -1);
    setAnnCount(annotationsRef.current.length);
    redraw(); commitShot();
  };
  const clearDrawing = () => {
    annotationsRef.current = []; setAnnCount(0);
    redraw(); commitShot();
  };

  const submit = async () => {
    if (!description.trim() && !title.trim()) { toast.error('Please describe the problem or request'); return; }
    setSubmitting(true);
    try {
      await api.submitFeedback({
        type, title, description, priority,
        screenshot: shot || '',
        attachment: attachment || null,
        page_url: window.location.pathname + window.location.search,
      });
      toast.success(type === 'bug' ? 'Bug report sent — thank you!' : 'Feature request sent — thank you!');
      reset();
      setOpen(false);
    } catch (e) {
      toast.error(e.message || 'Could not send feedback');
    } finally {
      setSubmitting(false);
    }
  };

  // Annotation toolbar (shared between inline + expanded views).
  const annToolbar = (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="flex items-center gap-1" data-testid="feedback-tools">
        {TOOLS.map(({ value, label, Icon }) => (
          <button key={value} onClick={() => { setTextDraft(null); setTool(value); }} title={label}
            className={`w-7 h-7 rounded-lg border flex items-center justify-center transition-all ${tool === value ? 'border-primary bg-primary/15 text-primary' : 'border-border/50 text-muted-foreground hover:bg-muted/30'}`}
            data-testid={`feedback-tool-${value}`}>
            <Icon className="w-3.5 h-3.5" />
          </button>
        ))}
      </div>
      <span className="w-px h-5 bg-border/50" />
      <div className="flex items-center gap-1.5">
        {PEN_COLORS.map(c => (
          <button key={c} onClick={() => setPenColor(c)} style={{ background: c }}
            className={`w-5 h-5 rounded-full border-2 transition-transform ${penColor === c ? 'border-foreground scale-110' : 'border-transparent'}`}
            data-testid={`feedback-pen-${c.replace('#', '')}`} aria-label={`pen ${c}`} />
        ))}
      </div>
      <div className="flex items-center gap-1 ml-auto">
        <Button size="sm" variant="ghost" onClick={undoAnnotation} disabled={annCount === 0} className="rounded-lg h-6 text-[11px] text-muted-foreground" data-testid="feedback-undo-annotation-btn">
          <Undo2 className="w-3 h-3 mr-1" /> Undo
        </Button>
        <Button size="sm" variant="ghost" onClick={clearDrawing} disabled={annCount === 0} className="rounded-lg h-6 text-[11px] text-muted-foreground" data-testid="feedback-clear-annotation-btn">
          <Eraser className="w-3 h-3 mr-1" /> Clear
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setExpanded(x => !x)} className="rounded-lg h-6 text-[11px] text-muted-foreground" data-testid="feedback-expand-btn">
          {expanded ? <><Minimize2 className="w-3 h-3 mr-1" /> Shrink</> : <><Maximize2 className="w-3 h-3 mr-1" /> Expand</>}
        </Button>
      </div>
    </div>
  );

  const canvasInner = (
    <>
      {annToolbar}
      <div className={`rounded-lg overflow-auto border border-border/40 bg-background/40 ${expanded ? 'flex-1 flex items-center justify-center w-full' : 'inline-block max-w-full'}`}>
        <div className="relative inline-block leading-none">
          <canvas
            ref={canvasRef}
            className="max-w-full touch-none cursor-crosshair block"
            onClick={canvasClick}
            onMouseDown={startDraw} onMouseMove={moveDraw} onMouseUp={endDraw} onMouseLeave={endDraw}
            onTouchStart={startDraw} onTouchMove={moveDraw} onTouchEnd={endDraw}
            data-testid="feedback-annotation-canvas"
          />
          {textDraft && (
            <input
              autoFocus
              value={textDraft.value}
              onChange={e => setTextDraft(d => ({ ...d, value: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter') commitText(); if (e.key === 'Escape') setTextDraft(null); }}
              onBlur={commitText}
              placeholder="Type, then Enter"
              style={{ position: 'absolute', left: textDraft.px, top: textDraft.py, color: penColor, zIndex: 5 }}
              className="text-sm font-semibold bg-background/90 border border-primary/50 rounded px-1 py-0.5 outline-none"
              data-testid="feedback-text-input"
            />
          )}
        </div>
      </div>
      <p className="text-[10px] text-muted-foreground">Pick a tool and draw on the screenshot to highlight the issue. Expand for a full-size view.</p>
    </>
  );

  const canvasBlock = expanded
    ? createPortal(
        <div className="fixed inset-0 z-[200] bg-background flex flex-col p-4 gap-3" data-testid="feedback-annotation-region" data-html2canvas-ignore>
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-foreground">Annotate screenshot — full screen</p>
            <Button size="sm" variant="outline" onClick={() => setExpanded(false)} className="rounded-lg h-7 text-xs" data-testid="feedback-collapse-btn">
              <Minimize2 className="w-3.5 h-3.5 mr-1" /> Done
            </Button>
          </div>
          {canvasInner}
        </div>,
        document.body)
    : (
      <div className="space-y-2" data-testid="feedback-annotation-region">
        {canvasInner}
      </div>
    );

  return (
    <>
      {/* Floating launcher — draggable. Tucked away while a blocking builder
          overlay (e.g. the Aggregate Wizard) is open so it can't overlap. */}
      {!overlayActive && (
      <button
        ref={btnRef}
        onPointerDown={startFabDrag}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(true); } }}
        data-html2canvas-ignore
        style={fabPos ? { right: fabPos.right, bottom: fabPos.bottom, left: 'auto', top: 'auto', touchAction: 'none', pointerEvents: 'auto' } : { touchAction: 'none', pointerEvents: 'auto' }}
        className={`fixed z-[300] w-6 h-6 rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 flex items-center justify-center hover:scale-105 active:scale-95 transition-transform cursor-grab active:cursor-grabbing ${fabPos ? '' : 'bottom-5 right-5 max-md:bottom-20'}`}
        data-testid="feedback-launcher"
        title="Report a bug or request a feature — drag to move"
      >
        <Plus className="w-3 h-3" />
      </button>
      )}

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent
          className="glass-card border-border/50 rounded-2xl sm:max-w-2xl max-h-[88vh] overflow-y-auto"
          data-testid="feedback-dialog"
          onInteractOutside={(e) => {
            // The expanded markup canvas is portaled to document.body (outside
            // this dialog's DOM subtree), so Radix sees clicks on it as "outside"
            // and would close the whole widget. Block outside-close while
            // annotating, and ignore any interaction inside the annotation region.
            const t = e.detail?.originalEvent?.target;
            if (expanded || t?.closest?.('[data-testid="feedback-annotation-region"]')) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => { if (expanded) { e.preventDefault(); setExpanded(false); } }}
        >
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Plus className="w-4 h-4 text-primary" /> Share feedback
            </DialogTitle>
          </DialogHeader>
          <DialogDescription className="sr-only">Report a bug or request a feature, optionally with an annotated screenshot.</DialogDescription>

          <div className="space-y-4 mt-2">
            {/* Type toggle */}
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setType('bug')}
                className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm transition-all ${type === 'bug' ? 'border-primary bg-primary/10 text-primary' : 'border-border/50 text-muted-foreground hover:bg-muted/30'}`}
                data-testid="feedback-type-bug"
              ><Bug className="w-4 h-4" /> Report a bug</button>
              <button
                onClick={() => setType('feature')}
                className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm transition-all ${type === 'feature' ? 'border-primary bg-primary/10 text-primary' : 'border-border/50 text-muted-foreground hover:bg-muted/30'}`}
                data-testid="feedback-type-feature"
              ><Lightbulb className="w-4 h-4" /> Request a feature</button>
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">Title (optional)</Label>
              <Input value={title} onChange={e => setTitle(e.target.value)} placeholder={type === 'bug' ? 'e.g. Chart not rendering on Insights' : 'e.g. Add dark export theme'} className="rounded-lg bg-muted/50 border-border/50 mt-1" data-testid="feedback-title-input" />
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">{type === 'bug' ? 'Describe the problem' : 'Describe the request'}</Label>
              <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder={type === 'bug' ? 'What happened? What did you expect? Steps to reproduce…' : 'What would you like to be able to do?'} className="rounded-lg bg-muted/50 border-border/50 mt-1 h-28" data-testid="feedback-description-input" />
            </div>

            {/* Priority — low→high heat scale */}
            <div>
              <Label className="text-xs text-muted-foreground flex items-center gap-1"><Flag className="w-3 h-3" /> Priority</Label>
              <div className="grid grid-cols-4 gap-2 mt-1" data-testid="feedback-priority">
                {PRIORITIES.map(p => {
                  const active = priority === p.value;
                  return (
                    <button
                      key={p.value}
                      onClick={() => setPriority(p.value)}
                      style={active
                        ? { borderColor: p.color, background: `${p.color}22`, color: p.color }
                        : {}}
                      className={`flex items-center justify-center gap-1.5 rounded-lg border px-2 py-1.5 text-xs font-medium transition-all ${active ? 'shadow-sm' : 'border-border/50 text-muted-foreground hover:bg-muted/30'}`}
                      data-testid={`feedback-priority-${p.value}`}
                    >
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: p.color, boxShadow: active ? `0 0 6px ${p.color}` : 'none' }} />
                      {p.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* File attachment */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs text-muted-foreground flex items-center gap-1"><Paperclip className="w-3 h-3" /> Attach a file (optional)</Label>
                {!attachment ? (
                  <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} className="rounded-lg h-7 text-xs" data-testid="feedback-attach-btn">
                    <Paperclip className="w-3.5 h-3.5 mr-1" /> Choose file
                  </Button>
                ) : (
                  <Button size="sm" variant="ghost" onClick={() => setAttachment(null)} className="rounded-lg h-7 text-xs text-muted-foreground" data-testid="feedback-remove-attachment-btn">
                    <X className="w-3.5 h-3.5 mr-1" /> Remove
                  </Button>
                )}
              </div>
              <input ref={fileInputRef} type="file" accept="image/*,application/pdf" onChange={onPickFile} className="hidden" data-testid="feedback-file-input" />
              {attachment && (
                <p className="text-[11px] text-foreground/80 truncate" data-testid="feedback-attachment-name">{attachment.name}</p>
              )}
              <p className="text-[10px] text-muted-foreground mt-0.5">Images or PDF, up to 10 MB.</p>
            </div>

            {/* Screenshot + annotation */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs text-muted-foreground">Screenshot (optional)</Label>
                <div className="flex items-center gap-2">
                  {!shot ? (
                    <Button size="sm" variant="outline" onClick={captureScreenshot} disabled={capturing} className="rounded-lg h-7 text-xs" data-testid="feedback-capture-btn">
                      <Camera className="w-3.5 h-3.5 mr-1" /> Capture screen
                    </Button>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => { setShot(null); setBaseShot(null); annotationsRef.current = []; setAnnCount(0); }} className="rounded-lg h-7 text-xs text-muted-foreground" data-testid="feedback-remove-shot-btn">
                      <X className="w-3.5 h-3.5 mr-1" /> Remove
                    </Button>
                  )}
                </div>
              </div>
              {shot && canvasBlock}
            </div>

            <Button onClick={submit} disabled={submitting} className="w-full rounded-lg bg-primary text-primary-foreground active:scale-95" data-testid="feedback-submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send {type === 'bug' ? 'bug report' : 'request'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
